import { useState, useEffect } from "react";
import { Send, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { Checkbox } from "../../components/ui/checkbox";
import { getWithdrawablesAPI, withdrawAPI } from "../../api/miningApi";
import { toast } from "react-toastify";

export default function WithdrawRequests() {
  const [allWithdrawables, setAllWithdrawables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [processingWithdraw, setProcessingWithdraw] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    fetchWithdrawData();
  }, []);

  const fetchWithdrawData = async () => {
    try {
      setLoading(true);
      const response = await getWithdrawablesAPI();
      if (response.success) {
        setAllWithdrawables(response.data || []);
      } else {
        toast.error(response.message || "Failed to fetch withdrawable users");
      }
    } catch (error) {
      console.error("Failed to fetch withdrawable users:", error);
      toast.error("Failed to fetch withdrawable users");
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    if (selectedIds.length === 0) {
      toast.warning("Please select at least one user to process withdrawal");
      return;
    }
    try {
      setProcessingWithdraw(true);
      const response = await withdrawAPI(selectedIds);
      if (response.success) {
        toast.success(`Successfully processed withdrawal for ${selectedIds.length} user(s)`);
        setSelectedIds([]);
        fetchWithdrawData();
      } else {
        toast.error(response.message || "Failed to process withdrawal");
      }
    } catch (error) {
      console.error("Failed to process withdrawal:", error);
      toast.error("Failed to process withdrawal");
    } finally {
      setProcessingWithdraw(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const pageUserIds = withdrawables.map((w) => w.userId);
      setSelectedIds((prev) => [...new Set([...prev, ...pageUserIds])]);
    } else {
      const pageUserIds = withdrawables.map((w) => w.userId);
      setSelectedIds((prev) => prev.filter((id) => !pageUserIds.includes(id)));
    }
  };

  const handleSelectOne = (userId, checked) => {
    if (checked) {
      setSelectedIds((prev) => [...prev, userId]);
    } else {
      setSelectedIds((prev) => prev.filter((id) => id !== userId));
    }
  };

  const totalRecords = allWithdrawables.length;
  const totalPages = Math.ceil(totalRecords / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const withdrawables = allWithdrawables.slice(startIndex, endIndex);
  const allSelectedOnPage =
    withdrawables.length > 0 && withdrawables.every((w) => selectedIds.includes(w.userId));

  const formatAmount = (amount) =>
    Number.isInteger(amount) ? amount : amount.toFixed(5);

  const handleExportCSV = () => {
    const dataToExport =
      selectedIds.length > 0
        ? allWithdrawables.filter((w) => selectedIds.includes(w.userId))
        : allWithdrawables;

    if (dataToExport.length === 0) {
      toast.warning("No data to export");
      return;
    }
    try {
      const csvContent = dataToExport
        .map((w) => `"${w.walletAddress}",${formatAmount(w.amount)}`)
        .join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      const fileName =
        selectedIds.length > 0
          ? `withdrawables_selected_${new Date().toISOString().split("T")[0]}.csv`
          : `withdrawables_all_${new Date().toISOString().split("T")[0]}.csv`;
      link.setAttribute("href", url);
      link.setAttribute("download", fileName);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      const message =
        selectedIds.length > 0
          ? `Exported ${selectedIds.length} selected user(s) successfully`
          : `Exported all ${dataToExport.length} user(s) successfully`;
      toast.success(message);
    } catch (error) {
      console.error("Failed to export CSV:", error);
      toast.error("Failed to export CSV");
    }
  };

  const renderPagination = () => {
    return Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
      if (
        page === 1 ||
        page === totalPages ||
        (page >= currentPage - 1 && page <= currentPage + 1)
      ) {
        return (
          <button
            key={page}
            onClick={() => setCurrentPage(page)}
            className={`h-8 w-8 rounded text-xs font-medium transition-colors ${
              page === currentPage
                ? "bg-ginox-green/20 border border-ginox-green/60 text-ginox-green"
                : "border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground"
            }`}
          >
            {page}
          </button>
        );
      }
      if (page === currentPage - 2 || page === currentPage + 2) {
        return (
          <span key={page} className="h-8 w-8 flex items-center justify-center text-muted-foreground text-xs">
            ...
          </span>
        );
      }
      return null;
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
          <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
          Mining
        </div>
        <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">Withdraw <span className="text-gradient-ginox">Requests</span></h1>
        <p className="text-sm text-muted-foreground">Process withdrawals for users with available balance</p>
      </div>

      {/* Action Bar */}
      <div className="p-5">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {selectedIds.length > 0 ? `${selectedIds.length} selected` : "No selection"}
          </span>
          <button
            onClick={handleWithdraw}
            disabled={selectedIds.length === 0 || processingWithdraw}
            className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            <Send className="w-3.5 h-3.5" />
            {processingWithdraw ? "Processing..." : "Process Withdrawal"}
          </button>
          <button
            onClick={handleExportCSV}
            disabled={selectedIds.length === 0}
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            <Download className="w-3.5 h-3.5" />
            Export Selected
          </button>
          <button
            onClick={() => setSelectedIds([])}
            disabled={selectedIds.length === 0}
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Clear Selection
          </button>
          {!loading && allWithdrawables.length > 0 && (
            <>
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-white/[0.05] text-muted-foreground border border-border/50">
                Total: {totalRecords}
              </span>
              <button
                onClick={handleExportCSV}
                className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors flex items-center gap-1.5 ml-auto"
              >
                <Download className="w-3.5 h-3.5" />
                {selectedIds.length > 0 ? `Export Selected (${selectedIds.length})` : "Export All"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <th className="border-b border-border/70 px-4 py-3 w-12">
                <Checkbox
                  checked={allSelectedOnPage}
                  onCheckedChange={handleSelectAll}
                  disabled={withdrawables.length === 0 || loading}
                />
              </th>
              {["User ID", "Name", "Username", "Amount", "Wallet Address"].map((h) => (
                <th key={h} className="border-b border-border/70 px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="py-12 text-center text-sm text-muted-foreground">Loading...</td>
              </tr>
            ) : withdrawables.length > 0 ? (
              withdrawables.map((withdrawable) => {
                const isSelected = selectedIds.includes(withdrawable.userId);
                return (
                  <tr key={withdrawable.userId} className="group hover:bg-white/[0.02] transition-colors">
                    <td className="border-b border-border/40 px-5 py-3.5">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => handleSelectOne(withdrawable.userId, checked)}
                      />
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                      {withdrawable.userId}
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">
                      {withdrawable.name}
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-muted-foreground">
                      @{withdrawable.username}
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-ginox-green font-mono font-semibold">
                      {formatAmount(withdrawable.amount)}
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-muted-foreground font-mono max-w-xs truncate">
                      {withdrawable.walletAddress}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={6} className="py-12 text-center text-sm text-muted-foreground">No withdrawable users found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col items-center gap-3 md:flex-row md:items-center md:justify-between">
          <p className="text-xs text-muted-foreground">
            {loading
              ? "Loading..."
              : `Showing ${startIndex + 1} to ${Math.min(endIndex, totalRecords)} of ${totalRecords} users`}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1 || loading}
              className="h-8 w-8 rounded border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            {!loading && renderPagination()}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || loading}
              className="h-8 w-8 rounded border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
